import React from "react";
import { NavLink } from "react-router-dom";
import {
  LayoutDashboard,
  Briefcase,
  Users,
  Bell,
  ScrollText,
  ShieldCheck,
  Sparkles,
  Wrench,
} from "lucide-react";
import { useAuth } from "../context/AuthContext";

// Phase 12 — sidebar grouped into sections, Linear-style precision
const SECTIONS = [
  {
    label: "Workspace",
    items: [
      { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, testid: "nav-dashboard" },
      {
        to: "/cases",
        label: "Cases",
        icon: Briefcase,
        testid: "nav-cases",
        roles: ["hr", "manager", "leadership", "compliance", "legal_review"],
      },
      {
        to: "/team-members",
        label: "Team Members",
        icon: Users,
        testid: "nav-team-members",
        roles: ["hr", "manager", "leadership", "compliance", "legal_review"],
      },
      { to: "/notifications", label: "Notifications", icon: Bell, testid: "nav-notifications" },
    ],
  },
  {
    label: "Insights",
    items: [
      {
        to: "/insights",
        label: "Insights",
        icon: Sparkles,
        roles: ["hr", "compliance", "leadership", "read_only_leadership", "legal_review"],
        testid: "nav-insights",
      },
    ],
  },
  {
    label: "Compliance",
    items: [
      {
        to: "/audit-logs",
        label: "Audit Log",
        icon: ScrollText,
        roles: ["hr", "compliance", "legal_review"],
        testid: "nav-audit-logs",
      },
      {
        to: "/compliance",
        label: "Compliance",
        icon: ShieldCheck,
        roles: ["compliance", "legal_review"],
        testid: "nav-compliance",
      },
    ],
  },
];

export function Sidebar({ unreadCount = 0 }) {
  const { user } = useAuth();
  const showAdmin =
    (user?.role === "hr" && user?.is_admin === true) || user?.role === "compliance";

  const filterItems = (items) =>
    items.filter((n) => !n.roles || n.roles.includes(user?.role));

  const visibleSections = SECTIONS
    .map((s) => ({ ...s, items: filterItems(s.items) }))
    .filter((s) => s.items.length > 0);

  return (
    <aside
      data-testid="sidebar"
      className="sticky top-0 hidden h-screen w-60 flex-shrink-0 flex-col md:flex"
      style={{
        backgroundColor: "#0E1F3A",
        borderRight: "1px solid rgba(255,255,255,0.06)",
      }}
    >
      {/* === brand lockup === */}
      <div className="px-4 pb-6 pt-6">
        <div className="flex items-center gap-2.5">
          <div
            data-testid="sidebar-brand-logo"
            className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg brand-logo-glow"
            style={{ backgroundColor: "rgba(255,255,255,0.05)" }}
          >
            <img
              src="/brand/goodwin-resolve-logo.png"
              alt="Goodwin Resolve"
              className="h-7 w-7 object-contain"
              draggable={false}
            />
          </div>
          <div className="leading-tight" data-testid="sidebar-wordmark">
            <span className="flex items-baseline gap-1.5">
              <span
                className="font-serif text-[16px] font-semibold leading-tight text-white"
                style={{ letterSpacing: "-0.005em" }}
              >
                Goodwin
              </span>
              <span
                className="text-[11px] font-extrabold uppercase tracking-[0.12em]"
                style={{ color: "#84C170" }}
              >
                Resolve
              </span>
            </span>
            <span
              className="mt-0.5 block text-[9px] font-medium uppercase tracking-[0.18em]"
              style={{ color: "rgba(255,255,255,0.72)" }}
              data-testid="sidebar-attribution"
            >
              by Goodwin Living
            </span>
          </div>
        </div>
      </div>

      {/* === nav sections === */}
      <nav className="flex flex-1 flex-col gap-0 overflow-y-auto px-2" data-testid="sidebar-nav">
        {visibleSections.map((section) => (
          <div key={section.label} className="mb-4">
            <div
              className="px-3 pb-1.5 pt-3 text-[10px] font-semibold uppercase tracking-[0.10em]"
              style={{ color: "rgba(255,255,255,0.66)" }}
            >
              {section.label}
            </div>
            <div className="flex flex-col gap-px">
              {section.items.map((item) => (
                <NavItem
                  key={item.to}
                  item={item}
                  unreadCount={unreadCount}
                />
              ))}
            </div>
          </div>
        ))}

        {showAdmin && (
          <div className="mb-4">
            <div
              className="px-3 pb-1.5 pt-3 text-[10px] font-semibold uppercase tracking-[0.10em]"
              style={{ color: "rgba(255,255,255,0.66)" }}
            >
              Admin
            </div>
            <NavItem
              item={{ to: "/admin", label: "Workspace", icon: Wrench, testid: "nav-admin" }}
              unreadCount={0}
              suffix={
                user?.role === "compliance" ? (
                  <span
                    data-testid="sidebar-admin-readonly-badge"
                    className="rounded-md px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wider"
                    style={{
                      backgroundColor: "rgba(132,193,112,0.16)",
                      color: "#84C170",
                    }}
                  >
                    Read
                  </span>
                ) : null
              }
            />
          </div>
        )}
      </nav>

      {/* === footer === */}
      <div
        className="px-4 py-4 text-[10px] leading-relaxed"
        style={{
          borderTop: "1px solid rgba(255,255,255,0.06)",
          color: "rgba(255,255,255,0.72)",
        }}
      >
        <div className="flex items-center gap-1.5">
          <span
            aria-hidden
            className="inline-block h-1 w-1 rounded-full"
            style={{ backgroundColor: "#5EAB45", boxShadow: "0 0 6px rgba(94,171,69,0.55)" }}
          />
          <span className="text-[10px] uppercase tracking-[0.16em]" style={{ color: "rgba(255,255,255,0.72)" }}>
            All systems normal
          </span>
        </div>
        <div className="mt-1.5" style={{ color: "rgba(255,255,255,0.62)" }}>
          Resolve today.
          <br />
          Strengthen tomorrow.
        </div>
      </div>
    </aside>
  );
}

function NavItem({ item, unreadCount, suffix }) {
  const Icon = item.icon;
  const showBadge = item.to === "/notifications" && unreadCount > 0;
  return (
    <NavLink
      to={item.to}
      data-testid={item.testid}
      className={({ isActive }) =>
        `group relative flex items-center gap-2.5 rounded-md px-3 py-1.5 text-sm transition-colors ${
          isActive ? "text-white" : "text-white/85 hover:text-white"
        }`
      }
      style={({ isActive }) => ({
        backgroundColor: isActive ? "rgba(255,255,255,0.05)" : "transparent",
      })}
    >
      {({ isActive }) => (
        <>
          {isActive && (
            <span
              aria-hidden
              className="absolute left-0 top-1/2 h-4 w-[2px] -translate-y-1/2 rounded-r"
              style={{ backgroundColor: "#5EAB45" }}
            />
          )}
          <Icon
            className="h-4 w-4 flex-shrink-0"
            strokeWidth={1.6}
            style={{
              color: isActive ? "#84C170" : "rgba(255,255,255,0.78)",
            }}
          />
          <span className="flex-1 truncate text-[13px]">{item.label}</span>
          {showBadge && (
            <span
              data-testid="sidebar-notifications-unread-badge"
              className="inline-flex h-4 min-w-[16px] items-center justify-center rounded-md px-1 text-[10px] font-semibold tabular-nums text-white"
              style={{ backgroundColor: "#DC2626" }}
            >
              {unreadCount > 99 ? "99+" : unreadCount}
            </span>
          )}
          {suffix}
        </>
      )}
    </NavLink>
  );
}

export default Sidebar;
